/**
 * Dev-mode flags - centralised read of the two opt-in toggles.
 *
 * Both default off, so:
 *   - Production CI builds (which never set the vars) get no dev code.
 *   - `npm run dev` against a real Entra tenant / running BFF is unchanged.
 *   - Vitest is unaffected (the test setup never imports this module).
 *
 * Read once at module load (`import.meta.env.*` is folded into a string
 * literal at build time by Vite's define plugin). The strict `=== 'true'`
 * check means anything other than the literal `"true"` is treated as off,
 * including the empty string and "false". That keeps a misconfigured CI
 * job (e.g. `VITE_USE_MOCK_AUTH=`) from accidentally shipping a mocked
 * production bundle.
 */
export const useMockAuth = import.meta.env.VITE_USE_MOCK_AUTH === 'true'
export const useMockBff = import.meta.env.VITE_USE_MOCK_BFF === 'true'

/**
 * Dev-only escape hatch: skip the `tnt` (tenant) authorisation check in the
 * SPA route guard. Use when working against a real Entra tenant whose token
 * does not yet carry the `tnt=suitsme` optional claim (single-tenant at M1).
 *
 * Double-gated and fail-safe:
 *   - hard off unless `VITE_ENV` is non-prod, so a prod build folds this to
 *     `false` and tree-shakes the branch even if the var leaks into prod CI;
 *   - strict `=== 'true'` literal, like the mock flags above.
 *
 * Scope: the SPA route guard only. The deployed BFF (the real trust
 * boundary) still enforces `tnt` unconditionally. The local BFF dev server
 * injects its own `tnt=suitsme` claims, so it is unaffected either way.
 */
export const skipTenantCheck =
  import.meta.env.VITE_ENV !== 'prod' && import.meta.env.VITE_SKIP_TENANT_CHECK === 'true'

/** True when any dev-mode mock is active. Used by the banner gate. */
export const devModeActive = useMockAuth || useMockBff

if (skipTenantCheck) {
  // Loud, once-per-load reminder that an auth control is relaxed.
  console.warn(
    '[dev] VITE_SKIP_TENANT_CHECK is ON - the SPA tenant (tnt) authorisation ' +
      'check is bypassed. Dev only; the deployed BFF still enforces tnt.',
  )
}
